import { useState, useEffect } from 'react';
import { BarChart3, Download, Calendar, BookOpen, Users, TrendingUp, CheckCircle, XCircle, Clock, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import api, { classApi } from '../lib/api';

// Status colors
const STATUS_COLORS = {
  suivi: { bg: 'bg-green-100', text: 'text-green-700', label: 'Suivi (حـ)' },
  non_suivi: { bg: 'bg-red-100', text: 'text-red-700', label: 'Non suivi (لم)' },
  absent: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Absent (غ)' },
  malade: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Malade (م)' },
};

// Grade colors
const GRADE_COLORS = {
  weak: { bg: 'bg-red-100', text: 'text-red-700', label: 'Faible (ضعيف)' },
  acceptable: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Acceptable (مقبول)' },
  good: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Bien (جيد)' },
  very_good: { bg: 'bg-green-100', text: 'text-green-700', label: 'Très bien (جيد جدا)' },
};

const MONTHS = [
  { value: 1, label: 'Janvier' }, { value: 2, label: 'Février' }, { value: 3, label: 'Mars' },
  { value: 4, label: 'Avril' }, { value: 5, label: 'Mai' }, { value: 6, label: 'Juin' },
  { value: 7, label: 'Juillet' }, { value: 8, label: 'Août' }, { value: 9, label: 'Septembre' },
  { value: 10, label: 'Octobre' }, { value: 11, label: 'Novembre' }, { value: 12, label: 'Décembre' },
];

export default function EvalDashboardPage() {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchClasses();
  }, []);

  useEffect(() => {
    fetchStats();
  }, [selectedClass]);

  const fetchClasses = async () => {
    try {
      const res = await classApi.getAll({});
      setClasses(res.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des classes');
    }
  };

  const fetchStats = async () => {
    setLoading(true);
    try {
      let url = '/evaluations/statistics';
      if (selectedClass) {
        url += `?class_id=${selectedClass}`;
      }
      const res = await api.get(url);
      setStats(res.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des statistiques');
    } finally {
      setLoading(false);
    }
  };

  const exportMonthlyPDF = async () => {
    if (!selectedClass) {
      toast.error('Veuillez sélectionner une classe');
      return;
    }
    setExporting(true);
    try {
      const response = await api.get(
        `/monthly-evals/pdf?class_id=${selectedClass}&month=${selectedMonth}&year=${selectedYear}`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `eval_mensuelle_${selectedMonth}_${selectedYear}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF téléchargé');
    } catch (error) {
      toast.error('Erreur lors de l\'export PDF');
    } finally {
      setExporting(false);
    }
  };

  const currentYear = new Date().getFullYear();
  const years = [currentYear - 1, currentYear, currentYear + 1];

  // Calculate percentages for visualization
  const getTotalDailyByStatus = () => {
    if (!stats?.daily?.by_status) return [];
    const total = Object.values(stats.daily.by_status).reduce((a, b) => a + b, 0);
    return Object.entries(stats.daily.by_status).map(([status, count]) => ({
      status,
      count,
      percentage: total > 0 ? Math.round((count / total) * 100) : 0,
      ...STATUS_COLORS[status]
    }));
  };

  const getGradeDistribution = () => {
    if (!stats?.trimestrial?.grade_distribution) return [];
    const total = Object.values(stats.trimestrial.grade_distribution).reduce((a, b) => a + b, 0);
    return Object.entries(stats.trimestrial.grade_distribution).map(([grade, count]) => ({
      grade,
      count,
      percentage: total > 0 ? Math.round((count / total) * 100) : 0,
      ...GRADE_COLORS[grade]
    }));
  };

  if (loading && !stats) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-12 w-full" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="eval-dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="w-7 h-7 text-primary" />
            Tableau de Bord - Évaluations
          </h1>
          <p className="text-muted-foreground text-sm">Statistiques et exports des évaluations coraniques</p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Filtrer par classe</Label>
              <Select value={selectedClass || 'all'} onValueChange={(v) => setSelectedClass(v === 'all' ? '' : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Toutes les classes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les classes</SelectItem>
                  {classes.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Mois (pour export)</Label>
              <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MONTHS.map(m => (
                    <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Année</Label>
              <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {years.map(y => (
                    <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>&nbsp;</Label>
              <Button 
                className="w-full" 
                onClick={exportMonthlyPDF}
                disabled={exporting || !selectedClass}
              >
                <Download className="w-4 h-4 mr-2" />
                {exporting ? 'Export...' : 'Export PDF Mensuel'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-500" />
              Évaluations Quotidiennes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-600">{stats?.daily?.total || 0}</div>
            <p className="text-sm text-muted-foreground">Total saisies</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {Object.entries(stats?.daily?.by_type || {}).map(([type, count]) => (
                <Badge key={type} variant="outline" className="text-xs">
                  {type}: {count}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="w-5 h-5 text-green-500" />
              Évaluations Mensuelles
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600">{stats?.monthly?.total || 0}</div>
            <p className="text-sm text-muted-foreground">Total saisies</p>
            <div className="mt-3">
              <Badge variant="secondary">
                Moy. absences: {stats?.monthly?.avg_absences || 0}
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-purple-500" />
              Évaluations Trimestrielles
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-purple-600">{stats?.trimestrial?.total || 0}</div>
            <p className="text-sm text-muted-foreground">Total élèves évalués</p>
          </CardContent>
        </Card>
      </div>

      {/* Status Distribution Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Répartition des Statuts (Quotidien)</CardTitle>
            <CardDescription>Distribution des évaluations quotidiennes par statut</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {getTotalDailyByStatus().map((item) => (
                <div key={item.status} className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="flex items-center gap-2">
                      {item.status === 'suivi' && <CheckCircle className="w-4 h-4 text-green-500" />}
                      {item.status === 'non_suivi' && <XCircle className="w-4 h-4 text-red-500" />}
                      {item.status === 'absent' && <Clock className="w-4 h-4 text-gray-500" />}
                      {item.status === 'malade' && <AlertCircle className="w-4 h-4 text-yellow-500" />}
                      {item.label}
                    </span>
                    <span className="font-medium">{item.count} ({item.percentage}%)</span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div 
                      className={`h-full ${item.bg} transition-all duration-500`}
                      style={{ width: `${item.percentage}%` }}
                    />
                  </div>
                </div>
              ))}
              {getTotalDailyByStatus().length === 0 && (
                <p className="text-center text-muted-foreground py-4">Aucune donnée disponible</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Distribution des Notes (Trimestriel)</CardTitle>
            <CardDescription>Répartition des notes sur les 12 parties</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {getGradeDistribution().map((item) => (
                <div key={item.grade} className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className={`${item.text} font-medium`}>{item.label}</span>
                    <span className="font-medium">{item.count} ({item.percentage}%)</span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div 
                      className={`h-full ${item.bg} transition-all duration-500`}
                      style={{ width: `${item.percentage}%` }}
                    />
                  </div>
                </div>
              ))}
              {getGradeDistribution().length === 0 && (
                <p className="text-center text-muted-foreground py-4">Aucune donnée disponible</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Links */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Accès Rapide</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Button variant="outline" className="h-auto py-4 flex-col" asChild>
              <a href="/quranic-daily-eval">
                <Calendar className="w-6 h-6 mb-2 text-blue-500" />
                <span>Éval. Quotidienne</span>
              </a>
            </Button>
            <Button variant="outline" className="h-auto py-4 flex-col" asChild>
              <a href="/monthly-eval">
                <Users className="w-6 h-6 mb-2 text-green-500" />
                <span>Éval. Mensuelle</span>
              </a>
            </Button>
            <Button variant="outline" className="h-auto py-4 flex-col" asChild>
              <a href="/trimestrial-eval">
                <BookOpen className="w-6 h-6 mb-2 text-purple-500" />
                <span>Éval. Trimestrielle</span>
              </a>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
